import React from 'react';
import {
  Box,
  Spinner,
  VStack,
  Text,
  Flex,
  SkeletonText,
  Skeleton,
  HStack,
  Grid,
} from '@chakra-ui/react';

interface LoadingSpinnerProps {
  variant?: 'spinner' | 'skeleton' | 'dots' | 'card' | 'list';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  text?: string;
  count?: number;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  variant = 'spinner',
  size = 'md',
  text = 'Loading...',
  count = 3
}) => {
  const spinnerSizes = {
    sm: 'sm',
    md: 'md',
    lg: 'lg',
    xl: 'xl'
  };

  if (variant === 'spinner') {
    return (
      <Flex direction="column" align="center" justify="center" p={8}>
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="brand.primary"
          size={spinnerSizes[size]}
          mb={4}
        />
        <Text color="gray.600" fontSize={size}>
          {text}
        </Text>
      </Flex>
    );
  }

  if (variant === 'dots') {
    return (
      <Flex direction="column" align="center" justify="center" p={8}>
        <HStack spacing={2} mb={4}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              w={3}
              h={3}
              bg="brand.primary"
              borderRadius="full"
              animation={`pulse 1.5s infinite ${i * 0.2}s`}
            />
          ))}
        </HStack>
        <Text color="gray.600" fontSize={size}>
          {text}
        </Text>
      </Flex>
    );
  }

  if (variant === 'card') {
    return (
      <Grid templateColumns="repeat(auto-fill, minmax(280px, 1fr))" gap={6} w="full">
        {Array.from({ length: count }).map((_, index) => (
          <Box
            key={index}
            bg="white"
            borderRadius="lg"
            shadow="md"
            overflow="hidden"
            border="1px solid"
            borderColor="gray.200"
          >
            {/* Image skeleton */}
            <Skeleton height="200px" />
            
            {/* Content skeleton */}
            <VStack p={4} align="start" spacing={3}>
              <Skeleton height="20px" width="80%" />
              <Skeleton height="16px" width="60%" />
              <Skeleton height="24px" width="40%" />
              <SkeletonText noOfLines={2} spacing={2} skeletonHeight="14px" />
              
              {/* Footer skeleton */}
              <HStack justify="space-between" w="full" pt={2}>
                <HStack spacing={2}>
                  <Skeleton w="20px" h="20px" borderRadius="full" />
                  <Skeleton height="14px" width="80px" />
                </HStack>
                <HStack spacing={2}>
                  <Skeleton height="32px" width="70px" borderRadius="md" />
                  <Skeleton height="32px" width="60px" borderRadius="md" />
                </HStack>
              </HStack>
            </VStack>
          </Box>
        ))}
      </Grid>
    );
  }

  if (variant === 'list') {
    return (
      <VStack spacing={4} w="full">
        {Array.from({ length: count }).map((_, index) => (
          <Box
            key={index}
            bg="white"
            borderRadius="lg"
            shadow="md"
            p={4}
            w="full"
            border="1px solid"
            borderColor="gray.200"
          >
            <HStack spacing={4} align="start">
              {/* Image skeleton */}
              <Skeleton
                w="120px"
                h="120px"
                borderRadius="md"
                flexShrink={0}
              />
              
              {/* Content skeleton */}
              <VStack align="start" flex={1} spacing={2}>
                <HStack justify="space-between" w="full">
                  <Skeleton height="20px" width="60%" />
                  <Skeleton height="20px" width="20%" />
                </HStack>
                
                <Skeleton height="16px" width="40%" />
                <SkeletonText noOfLines={2} spacing={2} skeletonHeight="14px" />
                
                <HStack justify="space-between" w="full" pt={2}>
                  <HStack spacing={4}>
                    <Skeleton height="14px" width="80px" />
                    <Skeleton height="14px" width="60px" />
                  </HStack>
                  <HStack spacing={2}>
                    <Skeleton w="32px" h="32px" borderRadius="md" />
                    <Skeleton w="32px" h="32px" borderRadius="md" />
                  </HStack>
                </HStack>
              </VStack>
            </HStack>
          </Box>
        ))}
      </VStack>
    );
  }

  // Default skeleton variant
  return (
    <VStack spacing={4} w="full" p={4}>
      <Skeleton height="40px" width="60%" />
      <SkeletonText noOfLines={4} spacing={4} skeletonHeight="20px" />
      <HStack spacing={4} w="full">
        <Skeleton height="32px" flex={1} />
        <Skeleton height="32px" flex={1} />
      </HStack>
    </VStack>
  );
};

export default LoadingSpinner; 